import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { MedplumClient } from "@medplum/core";
import { Observation } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { ActionIcon, Badge } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconPencil } from "@tabler/icons-react";

import { ProfileContext } from "@/contexts/profile";
import BloodTypeDrawer from "@/encounters/components/EncounterBloodType/BloodTypeDrawer";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { Systems } from "@/fhir/enums/systems";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { selectBloodType, selectHasBloodTypeData, setBloodType } from "@/store/slices/blood-type";
import { getCodeableConceptCode } from "@/utils/medplum";

import { ClientBadgesWrapper } from "./ClientBadgesWrapper";
import classes from "./ClientHealthSummary.module.css";
import { ClientHealthSummaryTypeEnum } from "./enums";

const ABO_CODE_TO_LETTER: Record<string, string> = {
  [LOINCCodes.ABOGroupA]: "A",
  [LOINCCodes.ABOGroupB]: "B",
  [LOINCCodes.ABOGroupO]: "O",
  [LOINCCodes.ABOGroupAB]: "AB",
};

const RH_CODE_TO_SIGN: Record<string, string> = {
  [LOINCCodes.RhPositive]: "+",
  [LOINCCodes.RhNegative]: "-",
};

const RH_CODE_TO_TEXT: Record<string, string> = {
  [LOINCCodes.RhPositive]: "Positive",
  [LOINCCodes.RhNegative]: "Negative",
};

interface BloodTypeComponents {
  aboLetter: string | null;
  rhSign: string | null;
  rhText: string | null;
}

async function getBloodTypeObservation(
  medplum: MedplumClient,
  patientId: string,
): Promise<Observation | null> {
  try {
    const result = await medplum.searchResources("Observation", {
      subject: `Patient/${patientId}`,
      code: `${Systems.LOINC}|${LOINCCodes.BloodTypePanel}`,
      status: "final",
      _sort: "-_lastUpdated",
      _count: "1",
    });

    const bundle =
      result?.bundle ||
      (Array.isArray(result)
        ? { entry: result.map((r) => ({ resource: r })), total: result.length }
        : result);

    const observations =
      bundle?.entry
        ?.map((entry) => entry.resource as Observation)
        .filter((obs) => obs?.resourceType === "Observation" && obs.status === "final") || [];

    return observations[0] || null;
  } catch {
    return null;
  }
}

function extractBloodTypeComponents(observation: Observation | null): BloodTypeComponents {
  if (!observation?.component || observation.component.length === 0) {
    return { aboLetter: null, rhSign: null, rhText: null };
  }

  let aboLetter: string | null = null;
  let rhSign: string | null = null;
  let rhText: string | null = null;

  for (const component of observation.component) {
    const componentCode = getCodeableConceptCode(component.code);
    const componentSystem = component.code?.coding?.[0]?.system;

    if (componentSystem === Systems.LOINC && componentCode === LOINCCodes.ABOGroup) {
      const aboValueCode = component.valueCodeableConcept?.coding?.[0]?.code;
      if (aboValueCode && ABO_CODE_TO_LETTER[aboValueCode]) {
        aboLetter = ABO_CODE_TO_LETTER[aboValueCode];
      }
    }

    if (componentSystem === Systems.LOINC && componentCode === LOINCCodes.RhType) {
      const rhValueCode = component.valueCodeableConcept?.coding?.[0]?.code;
      if (rhValueCode) {
        if (RH_CODE_TO_SIGN[rhValueCode]) {
          rhSign = RH_CODE_TO_SIGN[rhValueCode];
        }
        if (RH_CODE_TO_TEXT[rhValueCode]) {
          rhText = RH_CODE_TO_TEXT[rhValueCode];
        }
      }
    }
  }

  return { aboLetter, rhSign, rhText };
}

function getBloodTypeDisplay(aboLetter: string | null, rhSign: string | null): string | null {
  if (aboLetter && rhSign) {
    return `${aboLetter}${rhSign}`;
  }
  return null;
}

export default function ClientBloodType() {
  const medplum = useMedplum();
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId: string }>();

  const bloodType = useSelector(selectBloodType(clientId));
  const hasBloodTypeData = useSelector(selectHasBloodTypeData(clientId));

  const [isLoading, setIsLoading] = useState(() => !hasBloodTypeData && !!clientId);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  useEffect(() => {
    if (!clientId) {
      setIsLoading(false);
      return;
    }

    if (hasBloodTypeData) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    getBloodTypeObservation(medplum, clientId)
      .then((observation) => {
        if (!isMounted) return;
        const components = extractBloodTypeComponents(observation);
        const display = getBloodTypeDisplay(components.aboLetter, components.rhSign);
        dispatch(setBloodType({ patientId: clientId, bloodType: display }));
      })
      .catch((error) => {
        if (isMounted) {
          console.error("Failed to load blood type:", error);
          dispatch(setBloodType({ patientId: clientId, bloodType: null }));
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [clientId, medplum, dispatch, hasBloodTypeData]);

  const hasBloodType = bloodType !== null;
  const { isPatient } = useContext(ProfileContext);
  const { hasWriteRole } = usePractitionerPermissions();

  /** Blood type is chart data: Clients and Read-Only staff see the value but cannot change it. */
  const canEdit = !isPatient && hasWriteRole;

  const handleSuccess = async (bloodTypeValue: string | null) => {
    if (clientId) {
      dispatch(setBloodType({ patientId: clientId, bloodType: bloodTypeValue }));
    }
  };

  const editIcon = canEdit ? (
    <ActionIcon
      size="sm"
      variant="subtle"
      data-testid="client-blood-type-edit-icon"
      aria-label={hasBloodType ? "Edit blood type" : "Add blood type"}
      onClick={(e) => {
        e.stopPropagation();
        openDrawer();
      }}
    >
      <IconPencil size={16} />
    </ActionIcon>
  ) : undefined;

  if (isLoading) {
    return (
      <ClientBadgesWrapper
        noInfo={false}
        noneReported={false}
        title="Blood Type & Rh"
        summaryType={ClientHealthSummaryTypeEnum.BloodType}
      >
        <Badge
          px="md"
          py="sm"
          c="black"
          tt="none"
          bg="white"
          variant="filled"
          className={classes.patientBadge}
        >
          Loading...
        </Badge>
      </ClientBadgesWrapper>
    );
  }

  return (
    <>
      <ClientBadgesWrapper
        icon={editIcon}
        noneReported={false}
        noInfo={!hasBloodType}
        title="Blood Type & Rh"
        summaryType={ClientHealthSummaryTypeEnum.BloodType}
      >
        {hasBloodType && (
          <>
            <Badge
              px="md"
              py="sm"
              c="black"
              tt="none"
              bg="white"
              variant="filled"
              className={classes.patientBadge}
            >
              Blood Type: {bloodType}
            </Badge>
          </>
        )}
      </ClientBadgesWrapper>
      {clientId && canEdit && (
        <BloodTypeDrawer
          clientId={clientId}
          opened={drawerOpened}
          onClose={closeDrawer}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
}
